<template>
  <div class="view">
    <div class="header-container">
      <div class="view-gabarit">
        <h2>{{ $t('exportSection.header') }}</h2>
      </div>
    </div>
    <div class="form-container">
      <div class="view-gabarit">
        <div class="view-content">
          <div class="details-box">
            <h3>{{ pagination }} {{ $t('exportSection.transportModeTitle') }}</h3>
            <p class="baseline">{{ $t('exportSection.transportModeBaseline') }}</p>
          </div>

          <div class="form-gabarit">
            <div v-if="!currentExport.individual_transport_only" class="form-block">
              <div class="fieldset">
                <fieldset v-if="study.customTransport" class="site-gabarit">
                  <input id="custom-transport-enabled" v-model="currentExport.custom_transport_enabled" type="checkbox" />
                  <label for="custom-transport-enabled">
                    <b>{{ $t('transportMode.custom') }}</b>
                    <i>{{ $t('transportMode.customBaseline') }}</i>
                  </label>
                </fieldset>
                <fieldset v-if="!study.personalized_transport_only" class="site-gabarit">
                  <input id="optimized-transport-enabled" v-model="currentExport.optimized_transport_enabled" type="checkbox" />
                  <label for="optimized-transport-enabled">
                    <b>{{ $t('transportMode.optimized') }}</b>
                    <i>{{ $t('transportMode.optimizedBaseline') }}</i>
                  </label>
                </fieldset>
              </div>
            </div>
            <div v-if="!study.personalized_transport_only" class="form-block">
              <div class="fieldset">
                <fieldset v-if="!currentExport.individual_transport_only" class="site-gabarit">
                  <input id="public-transport-type" v-model="publicTransportType" type="checkbox" />
                  <label for="public-transport-type">
                    <b>{{ $t('transportMode.public') }}</b>
                    <i v-if="study.withWalkingTimes && study.maximum_travel_time_enabled !== false">
                      {{ $t('transportMode.publicBaseline', { minutes: study.public_maximum_travel_time }) }}
                    </i>
                  </label>
                  <div v-if="study.futur_transport_type > 0" class="radio-container">
                    <div class="radio-holder">
                      <input id="public" v-model="currentPublic" type="radio" value="1" />
                      <label for="public">{{ $t('transportMode.actuals') }}</label>
                    </div>

                    <div v-if="study.futur_transport_type === 1" class="radio-holder">
                      <input id="public2025" v-model="currentPublic" type="radio" value="2" />
                      <label for="public2025">{{ $t('transportMode.targetReach2025') }}</label>
                    </div>
                    <div v-if="study.futur_transport_type === 2" class="radio-holder">
                      <input id="public2030" v-model="currentPublic" type="radio" value="3" />
                      <label for="public2030">{{ $t('transportMode.targetReach2030') }}</label>
                    </div>

                    <div v-if="study.futur_transport_type === 3" class="radio-holder">
                      <input id="public2027" v-model="currentPublic" type="radio" value="4" />
                      <label for="public2027">{{ $t('transportMode.targetReach2027') }}</label>
                    </div>
                    <div v-if="study.futur_transport_type === 4" class="radio-holder">
                      <input id="public2031" v-model="currentPublic" type="radio" value="5" />
                      <label for="public2031">{{ $t('transportMode.targetReach2031') }}</label>
                    </div>

                    <div class="radio-holder">
                      <input id="both" v-model="currentPublic" type="radio" value="-1" />
                      <label for="both">{{ $t('transportMode.both') }}</label>
                    </div>
                  </div>
                </fieldset>

                <fieldset class="site-gabarit">
                  <input id="individual-transport-type" v-model="individualTransportType" type="checkbox" />
                  <label for="individual-transport-type">
                    <b>{{ $t('transportMode.private') }}</b>
                    <i v-if="study.withWalkingTimes && study.maximum_travel_time_enabled !== false">
                      {{ $t('transportMode.privateBaseline', { minutes: study.private_maximum_travel_time }) }}
                    </i>
                  </label>
                  <div v-if="!study.car_with_toll_only" class="radio-container">
                    <div class="radio-holder">
                      <input id="private" v-model="currentPrivate" type="radio" value="1" />
                      <label for="private">{{ $t('transportMode.withToll') }}</label>
                    </div>
                    <div class="radio-holder">
                      <input id="privateWithoutToll" v-model="currentPrivate" type="radio" value="2" />
                      <label for="privateWithoutToll">{{ $t('transportMode.withoutToll') }}</label>
                    </div>
                  </div>
                </fieldset>

                <fieldset v-if="!currentExport.individual_transport_only && currentExport.type !== ExportType.Short" class="site-gabarit">
                  <input id="bicycle-transport-enabled" v-model="currentExport.bicycle_transport_enabled" type="checkbox" />
                  <label for="bicycle-transport-enabled">
                    <b>{{ $t('transportMode.bicycle') }}</b>
                    <i v-if="study.withWalkingTimes && study.maximum_travel_time_enabled !== false">
                      {{ $t('transportMode.bicycleBaseline', { minutes: study.bicycle_maximum_travel_time }) }}
                    </i>
                  </label>
                </fieldset>

                <fieldset
                  v-if="study.withWalkingTimes && !currentExport.individual_transport_only && currentExport.type !== ExportType.Short"
                  class="site-gabarit"
                >
                  <input id="walking-transport-enabled" v-model="currentExport.walk_transport_enabled" type="checkbox" />
                  <label for="walking-transport-enabled">
                    <b>{{ $t('transportMode.walking') }}</b>
                    <i v-if="study.maximum_travel_time_enabled !== false">
                      {{ $t('transportMode.walkingBaseline', { minutes: study.walking_maximum_travel_time }) }}
                    </i>
                  </label>
                </fieldset>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="view-gabarit">
        <div class="cta-container">
          <button class="study-button" @click="() => previousStep()">{{ $t('previous') }}</button>
          <button class="study-button main" @click="() => nextStep()">
            {{ $t(currentExport.type === ExportType.Short ? 'previewYourExport' : 'continue') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ExportType from '@/pure/ExportType';

export default {
  computed: {
    ...mapGetters(['currentExport', 'study']),

    ExportType() {
      return ExportType;
    },

    currentPublic: {
      get() {
        return this.currentExport.public_transport_type.toString() !== '0' ? this.currentExport.public_transport_type.toString() : '1';
      },

      set(newVal) {
        this.currentExport.public_transport_type = newVal.toString();
        return newVal;
      },
    },

    publicTransportType: {
      get() {
        return this.currentExport.public_transport_type !== 0;
      },

      set(newVal) {
        this.currentExport.public_transport_type = newVal ? parseInt(this.currentPublic) : 0;
        return newVal;
      },
    },

    currentPrivate: {
      get() {
        return this.currentExport.individual_transport_type.toString() !== '0' ? this.currentExport.individual_transport_type.toString() : '1';
      },

      set(newVal) {
        this.currentExport.individual_transport_type = newVal.toString();
        return newVal;
      },
    },

    individualTransportType: {
      get() {
        return this.currentExport.individual_transport_type !== 0;
      },

      set(newVal) {
        this.currentExport.individual_transport_type = newVal ? parseInt(this.currentPrivate) : 0;
        return newVal;
      },
    },

    pagination() {
      switch (this.currentExport.type) {
        case ExportType.Simplified:
          return '2.';
        case ExportType.Specific:
          return '3.';
        case ExportType.Short:
        default:
          return '';
      }
    },
  },

  mounted() {
    if (this.study.personalized_transport_only) {
      this.currentExport.custom_transport_enabled = true;
    }
  },

  methods: {
    ...mapActions(['patchExport']),

    previousStep() {
      this.$router.push({
        name: this.currentExport.type === ExportType.Short ? 'studyList' : 'exportSites',
      });
    },

    nextStep() {
      const id = this.$route.params.studyId;
      const payload = this.study.personalized_transport_only
        ? {
            custom_transport_enabled: true,
            optimized_transport_enabled: false,

            public_transport_type: 0,
            individual_transport_type: 0,
            bicycle_transport_enabled: false,
            walk_transport_enabled: false,

            status: 4,
          }
        : {
            custom_transport_enabled: this.currentExport.custom_transport_enabled,
            optimized_transport_enabled: this.currentExport.optimized_transport_enabled,

            public_transport_type: this.currentExport.individual_transport_only ? 0 : parseInt(this.currentExport.public_transport_type, 10),
            individual_transport_type: parseInt(this.currentExport.individual_transport_type, 10),
            bicycle_transport_enabled: !this.currentExport.individual_transport_only && this.currentExport.bicycle_transport_enabled,
            walk_transport_enabled: !this.currentExport.individual_transport_only && this.study.withWalkingTimes && this.currentExport.walk_transport_enabled,

            status: 4,
          };

      this.patchExport({ id, payload }).then(() => {
        this.$router.push({
          name: this.currentExport.type === ExportType.Short ? 'exportConfiguration' : 'exportSections',
        });
      });
    },
  },
};
</script>

<style scoped lang="scss">
.radio-container {
  margin-top: 10px;
  margin-left: 40px;
  .radio-holder {
    & + .radio-holder {
      margin-top: 6px;
    }
  }
}
</style>
